import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { Button, Field, Notice, Select } from "../components";
import { Link } from "../router";

declare const __DEV__: boolean;

interface Ref { orgTypes: string[]; presets: Record<string, { code: string; name: string; kind: string }[]>; states: { code: string; name: string }[] }
interface Dept { code: string; name: string; kind: string }

const ORG_TYPE_LABELS: Record<string, string> = {
  clinic: "Clinic", nursing_home: "Nursing home", diagnostic_centre: "Diagnostic centre", day_care: "Day-care centre", hospital: "Hospital",
  multispeciality_hospital: "Multi-speciality hospital", dental_clinic: "Dental clinic", eye_hospital: "Eye hospital", ivf_centre: "IVF centre",
  physiotherapy_centre: "Physiotherapy centre", skin_clinic: "Skin / dermatology clinic", other: "Other",
};

const STEPS = [
  { title: "Organisation", fields: ["organisation.displayName", "organisation.legalName", "organisation.orgType", "organisation.phone", "organisation.email", "organisation.website"] },
  { title: "Location", fields: ["organisation.addressLine1", "organisation.addressLine2", "organisation.city", "organisation.stateCode", "organisation.pin", "organisation.gstin", "organisation.registrationNumber", "branches"] },
  { title: "Departments", fields: ["departments"] },
  { title: "Administrator", fields: ["admin.fullName", "admin.email", "admin.mobile"] },
  { title: "Review", fields: [] },
] as const;

const empty = {
  displayName: "", legalName: "", orgType: "", phone: "", email: "", website: "",
  addressLine1: "", addressLine2: "", city: "", stateCode: "", pin: "", gstin: "", registrationNumber: "",
  adminName: "", adminEmail: "", adminMobile: "",
};

function codeFromName(name: string, taken: Set<string>): string {
  const base = name.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 18) || "DEPT";
  let code = base.length >= 2 ? base : `${base}X`, i = 2;
  while (taken.has(code)) code = `${base.slice(0, 17)}${i++}`;
  return code;
}

export function Signup() {
  const [ref, setRef] = useState<Ref | null>(null);
  const [f, setF] = useState(empty);
  const [depts, setDepts] = useState<Dept[]>([]);
  const [deptTypeFor, setDeptTypeFor] = useState("");
  const [customDept, setCustomDept] = useState("");
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ sentTo: string; devLink?: string } | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => { api.get("/api/v1/signup/reference").then(setRef).catch(() => setFormError("Couldn't load the form. Reload the page.")); }, []);
  // Choosing a type pre-fills its typical departments (only when the type changes).
  useEffect(() => { if (ref && f.orgType && f.orgType !== deptTypeFor) { setDepts(ref.presets[f.orgType] ?? []); setDeptTypeFor(f.orgType); } }, [f.orgType, ref, deptTypeFor]);
  useEffect(() => { headingRef.current?.focus(); }, [step]);

  // Editing a field clears its error straight away; the next Continue re-validates everything on the step.
  const FIELD_PATH: Record<keyof typeof empty, string> = {
    displayName: "organisation.displayName", legalName: "organisation.legalName", orgType: "organisation.orgType", phone: "organisation.phone",
    email: "organisation.email", website: "organisation.website", addressLine1: "organisation.addressLine1", addressLine2: "organisation.addressLine2",
    city: "organisation.city", stateCode: "organisation.stateCode", pin: "organisation.pin", gstin: "organisation.gstin",
    registrationNumber: "organisation.registrationNumber", adminName: "admin.fullName", adminEmail: "admin.email", adminMobile: "admin.mobile",
  };
  const update = (k: keyof typeof empty, v: string) => {
    setF((x) => ({ ...x, [k]: v }));
    setErrors((e) => { if (!e[FIELD_PATH[k]] && !(k === "displayName" && e["organisation.legalName"])) return e;
      const n = { ...e }; delete n[FIELD_PATH[k]]; if (k === "displayName") delete n["organisation.legalName"]; return n; });
  };
  const set = (k: keyof typeof empty) => (e: { target: { value: string } }) => update(k, e.target.value);
  const err = (k: string) => errors[k];

  const payload = useMemo(() => ({
    organisation: { legalName: f.legalName || f.displayName, displayName: f.displayName, orgType: f.orgType, phone: f.phone, email: f.email,
      website: f.website || null, addressLine1: f.addressLine1, addressLine2: f.addressLine2 || null, city: f.city, stateCode: f.stateCode, pin: f.pin,
      gstin: f.gstin || null, registrationNumber: f.registrationNumber || null },
    departments: depts,
    admin: { fullName: f.adminName, email: f.adminEmail, mobile: f.adminMobile || null },
  }), [f, depts]);

  function stepErrors(all: Record<string, string>, i: number) {
    const keys = STEPS[i]!.fields as readonly string[];
    return Object.fromEntries(Object.entries(all).filter(([k]) => keys.some((p) => k === p || k.startsWith(p + "."))));
  }

  async function next(e: FormEvent) {
    e.preventDefault(); setFormError(null);
    if (step === 2 && depts.length === 0) { setErrors({ departments: "Keep at least one department. You can change them later." }); return; }
    setBusy(true);
    try {
      if (step < STEPS.length - 1) {
        const r = await api.post("/api/v1/signup/validate", payload);
        const mine: Record<string, string> = r.ok ? {} : stepErrors(r.errors, step);
        if (!f.legalName.trim()) delete mine["organisation.legalName"]; // optional: inherits the display name, whose own error is shown
        setErrors(mine);
        if (Object.keys(mine).length === 0) setStep(step + 1);
        else setTimeout(() => (document.querySelector("[aria-invalid=true]") as HTMLElement | null)?.focus(), 0);
      } else {
        const r = await api.post("/api/v1/signup", payload);
        let devLink: string | undefined;
        if (__DEV__) {
          const box = await api.get("/api/dev/outbox").catch(() => null);
          devLink = box?.messages?.filter((m: any) => m.to === f.adminEmail.trim().toLowerCase()).at(-1)?.link;
        }
        setDone({ sentTo: r.invitationSentTo, ...(devLink ? { devLink } : {}) });
      }
    } catch (ex) {
      const a = ex as ApiError;
      if (a.code === "validation_failed" && a.data?.errors) {
        setErrors(a.data.errors);
        const first = STEPS.findIndex((_, i) => Object.keys(stepErrors(a.data.errors, i)).length > 0);
        if (first >= 0) setStep(first);
      } else setFormError(a.message);
    } finally { setBusy(false); }
  }

  if (done) return (
    <div className="wizard-done">
      <main className="done-card">
        <h1 tabIndex={-1}>Check your email</h1>
        <p className="lede">We sent a link to <strong>{done.sentTo}</strong> to set the administrator's password. The link works once and expires in 72 hours.</p>
        <p>Nothing arrived? Check the spam folder, or contact support with your organisation's name.</p>
        {done.devLink && <Notice tone="info">Development mode: no email provider is connected. <a href={done.devLink} data-testid="dev-invite-link">Open the invitation link</a>.</Notice>}
        <Link to="/login" className="btn btn-secondary">Go to sign in</Link>
      </main>
    </div>
  );

  const stateName = ref?.states.find((s) => s.code === f.stateCode)?.name;
  return (
    <div className="wizard">
      <header className="wizard-top">
        <span className="wordmark">HealthOS</span>
        <span className="wizard-top-aside">Already set up? <Link to="/login">Sign in</Link></span>
      </header>
      <div className="wizard-body">
        <nav className="stepper" aria-label="Setup steps">
          <p className="step-count">Step {step + 1} of {STEPS.length}</p>
          <ol>
            {STEPS.map((s, i) => (
              <li key={s.title} className={i === step ? "current" : i < step ? "complete" : ""} aria-current={i === step ? "step" : undefined}>
                <span className="step-num" aria-hidden="true">{i < step ? "✓" : i + 1}</span>
                {i < step ? <button type="button" className="step-link" onClick={() => setStep(i)}>{s.title}</button> : <span>{s.title}</span>}
              </li>
            ))}
          </ol>
        </nav>

        <form className="wizard-form" onSubmit={next} noValidate>
          <h1 ref={headingRef} tabIndex={-1}>{
            ["Tell us about your organisation", "Where are you located?", "Which departments do you run?", "Who will administer HealthOS?", "Check and create"][step]}</h1>
          {formError && <Notice tone="error">{formError}</Notice>}
          {!ref && !formError && <p>Loading…</p>}

          {ref && step === 0 && <>
            <Field label="Name patients know you by" value={f.displayName} onChange={set("displayName")} error={err("organisation.displayName")} autoComplete="organization" />
            <Field label="Registered legal name" hint="As on your registration certificate. Leave blank if the same." optional value={f.legalName} onChange={set("legalName")} error={err("organisation.legalName")} />
            <fieldset className={`type-grid${err("organisation.orgType") ? " has-error" : ""}`} aria-invalid={err("organisation.orgType") ? true : undefined}>
              <legend>Type of organisation</legend>
              {ref.orgTypes.map((t) => (
                <label key={t} className={`type-option${f.orgType === t ? " selected" : ""}`}>
                  <input type="radio" name="orgType" value={t} checked={f.orgType === t} onChange={set("orgType")} />
                  {ORG_TYPE_LABELS[t] ?? t}
                </label>
              ))}
            </fieldset>
            {err("organisation.orgType") && <p className="error-text type-error">{err("organisation.orgType")}</p>}
            <div className="row">
              <Field label="Phone" type="tel" inputMode="tel" placeholder="98765 43210 or 011-23456789" value={f.phone} onChange={set("phone")} error={err("organisation.phone")} autoComplete="tel" />
              <Field label="Email" type="email" value={f.email} onChange={set("email")} error={err("organisation.email")} autoComplete="email" />
            </div>
            <Field label="Website" optional type="url" placeholder="https://" value={f.website} onChange={set("website")} error={err("organisation.website")} />
          </>}

          {ref && step === 1 && <>
            <Field label="Address" value={f.addressLine1} onChange={set("addressLine1")} error={err("organisation.addressLine1")} autoComplete="address-line1" />
            <Field label="Address line 2" optional value={f.addressLine2} onChange={set("addressLine2")} autoComplete="address-line2" />
            <div className="row">
              <Field label="City" value={f.city} onChange={set("city")} error={err("organisation.city")} autoComplete="address-level2" />
              <Field label="PIN code" inputMode="numeric" maxLength={6} value={f.pin} onChange={(e) => update("pin", e.target.value.replace(/\D/g, ""))} error={err("organisation.pin")} autoComplete="postal-code" />
            </div>
            <Select label="State or union territory" placeholder="Select" value={f.stateCode} onChange={set("stateCode")} error={err("organisation.stateCode")}
              options={ref.states.map((s) => ({ value: s.code, label: s.name }))} />
            <Field label="GSTIN" optional hint="Only if your organisation is GST-registered. Many clinics are not, because most healthcare services are GST-exempt."
              value={f.gstin} onChange={(e) => update("gstin", e.target.value.toUpperCase())} maxLength={15} error={err("organisation.gstin")} />
            <Field label="Clinical establishment registration number" optional value={f.registrationNumber} onChange={set("registrationNumber")} />
            {err("branches") && <Notice tone="error">{err("branches")}</Notice>}
            <p className="note">Your trial includes one location. You can add more branches when you choose a plan.</p>
          </>}

          {ref && step === 2 && <>
            <p className="lede">We've started with departments typical for a {ORG_TYPE_LABELS[f.orgType]?.toLowerCase() ?? "practice"}. Untick any you don't have, or add your own. You can change these later.</p>
            {err("departments") && <Notice tone="error">{err("departments")}</Notice>}
            <ul className="dept-list">
              {[...new Map([...(ref.presets[f.orgType] ?? []), ...depts].map((d) => [d.code, d])).values()].map((d) => {
                const on = depts.some((x) => x.code === d.code);
                return (
                  <li key={d.code}>
                    <label className="check">
                      <input type="checkbox" checked={on} onChange={() => setDepts((xs) => on ? xs.filter((x) => x.code !== d.code) : [...xs, d])} />
                      {d.name}
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="add-row">
              <Field label="Add a department" placeholder="e.g. Neurology" value={customDept} onChange={(e) => setCustomDept(e.target.value)} />
              <Button type="button" variant="secondary" disabled={customDept.trim().length < 2}
                onClick={() => { const name = customDept.trim(); setDepts((xs) => [...xs, { code: codeFromName(name, new Set(xs.map((x) => x.code))), name, kind: "clinical" }]); setCustomDept(""); }}>
                Add department
              </Button>
            </div>
          </>}

          {ref && step === 3 && <>
            <p className="lede">This person sets up doctors, staff and billing. We'll email them a link to choose a password.</p>
            <Field label="Full name" value={f.adminName} onChange={set("adminName")} error={err("admin.fullName")} autoComplete="name" />
            <Field label="Email" type="email" value={f.adminEmail} onChange={set("adminEmail")} error={err("admin.email")} autoComplete="email" />
            <Field label="Mobile number" optional type="tel" inputMode="tel" value={f.adminMobile} onChange={set("adminMobile")} error={err("admin.mobile")} autoComplete="tel" />
          </>}

          {ref && step === 4 && (
            <dl className="review">
              <div><dt>Organisation</dt><dd>{f.displayName}<br /><span className="muted">{ORG_TYPE_LABELS[f.orgType]}{f.legalName && f.legalName !== f.displayName ? `, registered as ${f.legalName}` : ""}</span></dd></div>
              <div><dt>Contact</dt><dd>{f.phone}<br />{f.email}</dd></div>
              <div><dt>Address</dt><dd>{f.addressLine1}{f.addressLine2 && <>, {f.addressLine2}</>}<br />{f.city}, {stateName} {f.pin}</dd></div>
              {f.gstin && <div><dt>GSTIN</dt><dd>{f.gstin}</dd></div>}
              <div><dt>Departments</dt><dd>{depts.map((d) => d.name).join(", ")}</dd></div>
              <div><dt>Administrator</dt><dd>{f.adminName}<br />{f.adminEmail}</dd></div>
            </dl>
          )}

          <div className="wizard-actions">
            {step > 0 && <Button type="button" variant="quiet" onClick={() => { setErrors({}); setStep(step - 1); }}>Back</Button>}
            <Button type="submit" busy={busy} disabled={!ref}>{step === STEPS.length - 1 ? "Create workspace" : "Continue"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
